import React from 'react';
import {Link} from 'react-router-dom';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  FaShoppingCart, FaEdit, FaEnvelope, FaCheckCircle, 
  FaInfinity, FaQrcode, FaMobileAlt, FaTruck, FaKey, 
  FaLink, FaRocket
} from 'react-icons/fa';

const {FiArrowRight, FiZap, FiShield, FiTrendingUp, FiMenu, FiX} = FiIcons;

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-red-600 selection:text-white">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50 animate-in fade-in slide-in-from-top duration-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <img 
                src="https://i.postimg.cc/28pHNw0g/Add-a-heading-3-removebg-preview-(1).png" 
                alt="ConnectKard" 
                className="w-10 h-10 object-contain hover:scale-110 transition-transform duration-300"
              />
              <span className="font-bold text-2xl tracking-tight leading-none">
                Connect<span className="text-red-500">Kard</span>
              </span>
            </div>
            <p className="text-[10px] font-bold tracking-wider mt-1 ml-1 leading-none">
              <span className="text-red-500">One QR</span> <span className="text-white">...</span> <span className="text-white">Infinite Connections</span> <span className="text-red-500">...</span>
            </p>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-10 text-sm font-semibold text-zinc-400">
            <Link to="/features" className="hover:text-white transition-colors duration-300">Features</Link>
            <Link to="/how-it-works" className="hover:text-white transition-colors duration-300">How It Works</Link>
            <Link to="/contact" className="hover:text-white transition-colors duration-300">Contact</Link>
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop Edit Card Button */}
            <Link to="/editqr" className="hidden sm:block">
              <Button variant="outline" className="px-6 py-2.5 text-sm bg-black/50 border-white/20 hover:bg-white/10 transition-all duration-300">
                <SafeIcon icon={FaEdit} /> Edit Card
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-white hover:text-red-500 transition-colors">
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md border-t border-white/10 animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-4">
              <Link to="/features" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors">
                Features
              </Link>
              <Link to="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors">
                How It Works
              </Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors">
                Contact
              </Link>
              <Link to="/editqr" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full py-3 text-sm bg-black/50 border-white/20 hover:bg-white/10 transition-all duration-300">
                  <SafeIcon icon={FaEdit} /> Edit Card
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Full Screen Spline */}
      <section className="relative h-screen min-h-[700px] overflow-hidden flex items-center">
        {/* Spline Background Iframe */}
        <div className="absolute inset-0 w-full h-[120%] z-0 top-0">
          <iframe src='https://my.spline.design/connectingcard-SzutvHS8tNxwHlx3bgePOxLo/' frameBorder='0' width='100%' height='100%' className="w-full h-full" title="ConnectKard 3D Model" />
        </div>
        
        {/* Gradient Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent z-0 pointer-events-none" />

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full pt-10">
          <div className="max-w-2xl space-y-8 animate-in fade-in slide-in-from-left duration-1000">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600/20 border border-red-500/30 rounded-full text-red-400 text-sm font-bold backdrop-blur-md shadow-lg shadow-red-900/20 animate-pulse">
              <SafeIcon icon={FiZap} />
              <span>Modern Networking Solution</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[1.1] drop-shadow-2xl">
               Your Business <br />
               <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-orange-400 animate-in fade-in slide-in-from-bottom duration-1000 delay-200">
                 Reimagined
               </span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-300 max-w-xl leading-relaxed drop-shadow-lg animate-in fade-in slide-in-from-bottom duration-1000 delay-300">
              Smart business cards that grow with you. Update anytime, share instantly, connect seamlessly.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-6 animate-in fade-in slide-in-from-bottom duration-1000 delay-500">
              <a href="https://wa.me/917796983999?text=I%20want%20to%20buy%20ConnectKard" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto px-10 py-5 text-lg font-bold shadow-2xl shadow-red-900/40 hover:shadow-red-900/60 transform hover:scale-105 transition-all duration-300">
                  <SafeIcon icon={FaShoppingCart} /> Buy Now <SafeIcon icon={FiArrowRight} />
                </Button>
              </a>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto px-10 py-5 text-lg font-bold border-2 bg-black/40 backdrop-blur-sm border-zinc-500 hover:bg-white/10 hover:border-white text-white transition-all duration-300">
                  <SafeIcon icon={FaEnvelope} /> Contact Us
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-8 pt-8 text-sm text-zinc-400 font-medium animate-in fade-in slide-in-from-bottom duration-1000 delay-700">
              <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg backdrop-blur-sm border border-white/5 hover:border-green-500/30 transition-all duration-300">
                <SafeIcon icon={FaCheckCircle} className="text-green-500 text-lg" />
                <span>Instant Updates</span>
              </div>
              <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg backdrop-blur-sm border border-white/5 hover:border-green-500/30 transition-all duration-300">
                <SafeIcon icon={FaCheckCircle} className="text-green-500 text-lg" />
                <span>No App Required</span>
              </div>
              <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg backdrop-blur-sm border border-white/5 hover:border-green-500/30 transition-all duration-300">
                <SafeIcon icon={FaCheckCircle} className="text-green-500 text-lg" />
                <span>Eco-Friendly</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-zinc-950 relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 md:mb-20 animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              Why Choose ConnectKard?
            </h2>
            <p className="text-zinc-400 text-xl max-w-2xl mx-auto mb-8">
              Everything you need to make lasting professional connections
            </p>
            <Link to="/features">
              <Button variant="outline" className="px-8 py-3 text-base border-2 border-red-500/50 text-red-500 hover:bg-red-500/10 hover:border-red-500">
                View All Features <SafeIcon icon={FiArrowRight} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {icon: FaInfinity, title: "Unlimited Updates", description: "Change your contact info, social links, or business details anytime without reprinting cards.", delay: "delay-100"},
              {icon: FaMobileAlt, title: "Instant Sharing", description: "Simple tap or scan to share everything. Works with any smartphone, no app installation needed.", delay: "delay-200"},
              {icon: FiShield, title: "Secure & Private", description: "Your data is protected with enterprise-grade security. Only you control what you share.", delay: "delay-300"},
              {icon: FiTrendingUp, title: "Analytics Included", description: "Track how many people scan your card and measure your networking success.", delay: "delay-400"},
              {icon: FaQrcode, title: "Multi-Link Profile", description: "Share multiple links at once - website, social media, portfolio, and more in one place.", delay: "delay-500"},
              {icon: FiZap, title: "Eco-Friendly", description: "Reduce waste with one reusable card. Better for you and the planet.", delay: "delay-600"}
            ].map((feature, index) => (
              <div key={index} className={`bg-zinc-900/50 border border-zinc-800 rounded-3xl p-10 hover:border-red-500/50 transition-all duration-500 group backdrop-blur-sm hover:bg-zinc-900 hover:-translate-y-2 animate-in fade-in slide-in-from-bottom ${feature.delay}`}>
                <div className="w-16 h-16 bg-red-600/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-red-600 transition-all duration-300 shadow-lg group-hover:scale-110">
                  <SafeIcon icon={feature.icon} className="text-4xl text-red-500 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-zinc-400 leading-relaxed text-lg">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 md:py-32 bg-black relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 md:mb-20 animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              How It Works
            </h2>
            <p className="text-zinc-400 text-xl max-w-2xl mx-auto mb-8">
              Get started in 6 simple steps
            </p>
            <Link to="/how-it-works">
              <Button variant="outline" className="px-8 py-3 text-base border-2 border-red-500/50 text-red-500 hover:bg-red-500/10 hover:border-red-500">
                View Detailed Guide <SafeIcon icon={FiArrowRight} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {[
              {number: 1, icon: FaShoppingCart, title: "Buy Your Card", description: "Choose your design and place your order via WhatsApp.", rotate: "-rotate-6", delay: "delay-100"},
              {number: 2, icon: FaTruck, title: "Get It Delivered", description: "We'll ship your premium smart card directly to your doorstep.", rotate: "", delay: "delay-200"},
              {number: 3, icon: FaEdit, title: "Click Edit Card", description: "Navigate to the Edit Card section on our website.", rotate: "rotate-6", delay: "delay-300"},
              {number: 4, icon: FaKey, title: "Enter Your Code", description: "Add the 6-digit OTP from your card, or scan the QR code if it's your first time.", rotate: "-rotate-3", delay: "delay-400"},
              {number: 5, icon: FaLink, title: "Add Your URL", description: "Enter your website, social media, or any destination link.", rotate: "", delay: "delay-500"},
              {number: 6, icon: FaRocket, title: "You're Ready!", description: "Save your settings and start sharing your smart card instantly.", rotate: "rotate-3", delay: "delay-600"}
            ].map((step, index) => (
              <div key={index} className={`text-center space-y-6 animate-in fade-in slide-in-from-bottom ${step.delay} hover:scale-105 transition-transform duration-300`}>
                <div className={`w-24 h-24 bg-gradient-to-br from-red-600 to-red-800 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-red-900/40 transform ${step.rotate} hover:rotate-0 transition-all duration-300 relative overflow-hidden group`}>
                   <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                   <SafeIcon icon={step.icon} className="text-3xl text-white relative z-10" />
                </div>
                <div className="bg-zinc-900/30 border border-zinc-800 rounded-2xl p-6 hover:border-red-500/30 transition-all duration-300">
                   <div className="inline-block bg-red-600/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold mb-3">
                     Step {step.number}
                   </div>
                   <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                   <p className="text-zinc-400 leading-relaxed">
                     {step.description}
                   </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-red-950/30 via-black to-zinc-950 relative overflow-hidden z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent animate-pulse" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative animate-in fade-in slide-in-from-bottom duration-700">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-8">
            Ready to Upgrade Your Networking?
          </h2>
          <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Join professionals who have made the switch to smart business cards.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="https://wa.me/917796983999?text=I%20want%20to%20buy%20ConnectKard" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto px-12 py-6 text-xl font-bold shadow-2xl shadow-red-900/50 hover:shadow-red-900/70 transform hover:scale-105 transition-all duration-300 animate-in fade-in slide-in-from-bottom delay-200">
                <SafeIcon icon={FaShoppingCart} /> Get Your Card Now <SafeIcon icon={FiArrowRight} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/50 bg-zinc-950 py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3">
                <img 
                  src="https://i.postimg.cc/28pHNw0g/Add-a-heading-3-removebg-preview-(1).png" 
                  alt="ConnectKard" 
                  className="w-10 h-10 object-contain"
                />
                <span className="font-bold text-xl leading-none">
                  Connect<span className="text-red-500">Kard</span>
                </span>
              </div>
              <p className="text-[10px] font-bold tracking-wider mt-1 ml-1 leading-none">
                <span className="text-red-500">One QR</span> <span className="text-white">...</span> <span className="text-white">Infinite Connections</span> <span className="text-red-500">...</span>
              </p>
            </div>
            <p className="text-zinc-500 text-sm">
              © 2026 ConnectKard. All rights reserved.
            </p>
            <div className="flex gap-8 text-sm text-zinc-400 font-medium">
              <a href="#" className="hover:text-white transition-colors duration-300">Privacy</a>
              <a href="#" className="hover:text-white transition-colors duration-300">Terms</a>
              <Link to="/admin/login" className="hover:text-white transition-colors duration-300">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;